import { Button, ChevronLeft, useIsScrolled } from '../../../modules/design-system';

export interface DeliveryHeaderProps {
  title: string;
  onBack: () => void;
}

/**
 * Top bar shared by the Delivery New / Delivery Show pages. Mirrors the
 * MyPacksHeader 3-slot layout: icon-only back button on the left, centred
 * title, mirrored empty slot on the right (so the title is geometrically
 * centred at @[600px]+). The back button reuses the heavy `glass-frosted`
 * surface from the earlier text-pill design — squared + pill radius gives
 * the same visual weight as a circle, so the Design Constructor's
 * `--co-Button-glass-frosted*` knobs continue to theme it.
 */
export function DeliveryHeader({ title, onBack }: DeliveryHeaderProps) {
  const scrolled = useIsScrolled(8);
  return (
    <header
      data-co-component="DeliveryHeader"
      data-scrolled={scrolled || undefined}
      className={
        'sticky top-0 z-30 header-safe-top ' +
        'transition-[background-color,backdrop-filter,border-color] duration-200 ease-out ' +
        (scrolled
          ? 'bg-black/40 backdrop-blur-md border-b border-white/10 '
          : 'border-b border-transparent ')
      }
      style={{
        // Delivery namespace is fully independent of MyPacks — only the
        // own knob is consulted, with the Inter system stack as the
        // hardcoded default.
        fontFamily: 'var(--co-DeliveryHeader-fontFamily, Inter), system-ui, sans-serif',
      }}
    >
      <div
        className="
          mx-auto flex w-full max-w-350 items-center gap-3
          px-4 py-4 @[600px]:px-6 @[600px]:py-5
        "
      >
        <div className="flex flex-1 items-center justify-start">
          <Button
            variant="glass-frosted"
            size="lg"
            onClick={onBack}
            aria-label="Back"
            className="!h-9 !w-9 !px-0 !gap-0 @[600px]:!h-11 @[600px]:!w-11"
          >
            <ChevronLeft size={20} strokeWidth={2} />
          </Button>
        </div>

        <h1
          className="flex-1 text-center font-bold tracking-[-0.3px]"
          style={{
            color: 'var(--co-DeliveryHeader-titleColor, #FFFFFF)',
            fontSize: 'var(--co-DeliveryHeader-titleSize, 18px)',
          }}
        >
          {title}
        </h1>

        {/* Right spacer mirrors the back-slot width so the title stays
         *  geometrically centred on every breakpoint. */}
        <div aria-hidden className="flex flex-1" />
      </div>
    </header>
  );
}
